import React, { useEffect } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate, useOutlet } from 'react-router-dom';
import { PagesRoutes } from '../../store/routes';
import { Dashboard } from '../dashboard/dashboard';
import { preloadReflinkHeroImages } from './reflinkShared';

/** Same overlay treatment as `LandPopup`: dimmed glass over the main page. */
export const ReflinkOverlayLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = useOutlet();

  useEffect(() => {
    preloadReflinkHeroImages();
  }, []);

  const handleBackdropClick = () => {
    navigate(PagesRoutes.Main, { replace: true });
  };

  const stopBackdropClose = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Root>
      <BackdropMain aria-hidden>
        <Dashboard />
      </BackdropMain>
      <Overlay
        role="dialog"
        aria-modal="true"
        aria-label="Referral link"
        onClick={handleBackdropClick}
      >
        <PopupFrame onClick={stopBackdropClose}>
          <ReflinkOutletStage>
            <AnimatePresence initial={false}>
              <ReflinkOutletLayer
                key={location.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
              >
                {outlet}
              </ReflinkOutletLayer>
            </AnimatePresence>
          </ReflinkOutletStage>
        </PopupFrame>
      </Overlay>
    </Root>
  );
};

const Root = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: calc(var(--vh, 1vh) * 100);
`;

const BackdropMain = styled.div`
  flex: 1;
  width: 100%;
  pointer-events: none;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  @media (max-width: 640px) {
    padding: 16px 8px;
  }
`;

const PopupFrame = styled.div`
  width: min(100%, 415px);
  display: flex;
  justify-content: center;
`;

const ReflinkOutletStage = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;

  & > * {
    grid-column: 1;
    grid-row: 1;
    min-width: 0;
  }
`;

const ReflinkOutletLayer = styled(motion.div)`
  width: 100%;
  min-width: 0;
`;
